import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Divider, Alert, Grid, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip,
  Checkbox, Autocomplete, CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import * as shipmentMgmtApi from '../../lib/api/shipmentMgmt';
import * as projectApi from '../../lib/api/project';

/**
 * 출고 등록 — 프로젝트를 정하고 내보낼 것을 담는다.
 *
 * 생산품은 출고용 창고를 거치므로, 내보낼 것은 언제나 창고 재고다.
 * 프로젝트를 고르면 그 프로젝트 것으로 잡힌 재고만 나오고, 체크한 줄이
 * 출고 한 건의 내역이 된다. 여러 줄을 담으면 섞인 출고가 된다.
 *
 * 어느 생산계획 물건인지는 재고가 들고 있어서 사람이 고를 필요가 없다.
 * 예전에는 화면이 열려 있던 계획이 붙어서, 창고에 여러 계획 물건이 섞이면 장부가 틀렸다.
 */
const ShipmentMgmtCreateDialog = ({ open, onClose, onSave, saving = false, saveError = null }) => {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [shipmentDate, setShipmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');

  const [stocks, setStocks] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  // stockUuid -> 보낼 수량. 담지 않은 줄은 여기 없다.
  const [picked, setPicked] = useState({});

  useEffect(() => {
    if (!open) return;
    setProject(null);
    setStocks([]);
    setPicked({});
    setRemarks('');
    setShipmentDate(new Date().toISOString().split('T')[0]);

    projectApi.getProjects({})
      .then((res) => setProjects(res.data?.list || []))
      .catch(() => setProjects([]));
  }, [open]);

  const loadStocks = useCallback(async (prjUuid) => {
    if (!prjUuid) {
      setStocks([]);
      setPicked({});
      return;
    }
    setLoadingStocks(true);
    try {
      const res = await shipmentMgmtApi.getShippableStock(prjUuid);
      setStocks(res.data?.data || []);
      setPicked({});
    } catch (err) {
      setStocks([]);
      setPicked({});
    } finally {
      setLoadingStocks(false);
    }
  }, []);

  const handleProjectChange = (e, value) => {
    setProject(value);
    loadStocks(value?.prjUuid);
  };

  const toggle = (stock) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[stock.stockUuid] !== undefined) {
        delete next[stock.stockUuid];
      } else {
        // 대개 있는 것을 다 보낸다. 줄여야 하면 그 자리에서 고친다.
        next[stock.stockUuid] = Number(stock.productCnt) || 1;
      }
      return next;
    });
  };

  const changeQty = (stock, value) => {
    setPicked((prev) => ({ ...prev, [stock.stockUuid]: value }));
  };

  const pickedRows = stocks.filter((s) => picked[s.stockUuid] !== undefined);
  const totalQty = pickedRows.reduce((sum, s) => sum + (Number(picked[s.stockUuid]) || 0), 0);

  /** 담은 줄 중 보낼 수 없는 것이 있으면 무엇이 문제인지 알려준다. */
  const problem = (() => {
    if (!project) return '프로젝트를 고르세요.';
    if (pickedRows.length === 0) return '내보낼 항목을 하나 이상 담으세요.';
    for (const s of pickedRows) {
      const qty = Number(picked[s.stockUuid]);
      const stockQty = Number(s.productCnt) || 0;
      if (!qty || Number.isNaN(qty) || qty <= 0) return `${s.productNm}: 보낼 수량을 넣으세요.`;
      if (qty > stockQty) return `${s.productNm}: 재고(${stockQty})보다 많이 보낼 수 없습니다.`;
    }
    return '';
  })();

  const handleSubmit = () => {
    if (problem) return;

    // 첫 줄을 헤더의 대표 값으로 쓴다. 상세가 진짜이고 헤더는 요약이다.
    const head = pickedRows[0];
    onSave({
      sourceType: 'WAREHOUSE',
      shipmentDate,
      remarks: remarks.trim() || null,
      // 생산계획과 고객사는 서버가 재고의 주인에서 채운다. 여기서 보내지 않는다.
      stockUuid: head.stockUuid,
      whUuid: head.whUuid,
      productUuid: head.productUuid,
      quantity: totalQty,
      details: pickedRows.map((s) => ({
        productUuid: s.productUuid,
        stockUuid: s.stockUuid,
        whUuid: s.whUuid,
        quantity: Number(picked[s.stockUuid]),
        remarks: null,
      })),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" />
          <Typography variant="h6">출고 등록</Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={projects}
              value={project}
              onChange={handleProjectChange}
              getOptionLabel={(o) => o?.cstNm || o?.prjNm || ''}
              isOptionEqualToValue={(o, v) => o.prjUuid === v?.prjUuid}
              renderInput={(params) => <TextField {...params} label="프로젝트" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="고객사"
              value={project?.customerNm || ''}
              placeholder="프로젝트를 고르면 채워집니다"
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" type="date" label="출고일"
              value={shipmentDate}
              onChange={(e) => setShipmentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined">
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', backgroundColor: 'action.hover',
          }}>
            <Typography variant="subtitle2" fontWeight={700}>이 프로젝트의 창고 재고</Typography>
            <Typography variant="body2" color="text.secondary">
              {pickedRows.length}건 담음 · {totalQty}개
            </Typography>
          </Box>

          {loadingStocks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>품목</TableCell>
                    <TableCell>창고</TableCell>
                    <TableCell>생산계획</TableCell>
                    <TableCell align="right">재고</TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>보낼 수량</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stocks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                        {project
                          ? '이 프로젝트 것으로 창고에 있는 재고가 없습니다.'
                          : '프로젝트를 고르면 내보낼 수 있는 재고가 나옵니다.'}
                      </TableCell>
                    </TableRow>
                  ) : stocks.map((s) => {
                    const on = picked[s.stockUuid] !== undefined;
                    return (
                      <TableRow key={s.stockUuid} hover selected={on}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={on} onChange={() => toggle(s)} />
                        </TableCell>
                        <TableCell>
                          {s.productNm}
                          {s.modelNm ? <Typography variant="caption" color="text.secondary"> / {s.modelNm}</Typography> : null}
                        </TableCell>
                        <TableCell>{s.whNm}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" color="primary" label={s.planNumber || '-'} />
                        </TableCell>
                        <TableCell align="right">{s.productCnt}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small" variant="standard" type="number"
                            value={on ? picked[s.stockUuid] : ''}
                            onChange={(e) => changeQty(s, e.target.value)}
                            disabled={!on}
                            inputProps={{ min: 1, max: Number(s.productCnt) || 1, style: { textAlign: 'right' } }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          어느 생산계획 물건인지는 재고가 들고 있어서 따로 고르지 않아도 됩니다.
          여러 줄을 담으면 한 건으로 함께 나갑니다.
        </Typography>

        <TextField
          fullWidth size="small" label="비고" sx={{ mt: 2 }}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="선택"
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="error.main">{problem}</Typography>
        <Box>
          <Button onClick={onClose} color="inherit" disabled={saving} sx={{ mr: 1 }}>취소</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || Boolean(problem)}>
            {saving ? '내보내는 중...' : `${pickedRows.length}건 출고`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentMgmtCreateDialog;
